import { LIB, LIB_ORDER } from './library';
import { guessFn } from './pdfDevices';
import type { BBox, Project, SymDef } from './types';
import { t as $t, tk } from './i18n';


/*
 * User-drawn symbols. They are stored as validated primitives, never as raw
 * SVG, and the SVG is generated here — so a symbol file cannot inject markup.
 */

export type Prim =
  | { k: 'line'; pts: [number, number][]; dash?: boolean }
  | { k: 'rect'; x: number; y: number; w: number; h: number; fill?: boolean }
  | { k: 'circle'; x: number; y: number; r: number; fill?: boolean }
  | { k: 'text'; x: number; y: number; size: number; text: string };

export type CustomRole = 'main' | 'aux' | 'plc' | 'term';

export interface CustomPin {
  x: number;
  y: number;
  label: string;
}

export interface CustomSymbol {
  /** "u_" + id */
  key: string;
  name: string;
  cat: string;
  prefix: string;
  role: CustomRole;
  kind?: 'no' | 'nc';
  mirror?: boolean;
  pins: CustomPin[];
  /** pin index pairs a potential passes through */
  pass?: [number, number][];
  /** the library symbol it stands for (e.g. "coil" for an imported contactor coil) */
  fn?: string;
  prims: Prim[];
}

export class SymbolFormatError extends Error {}

export const CUSTOM_CAT = tk("Kullanıcı sembolleri");
export const isCustomKey = (k: string) => /^u_[a-z0-9_]{1,40}$/.test(k);

const LIMIT = 400;
const n = (v: unknown, what: string) => {
  const x = Number(v);
  if (!Number.isFinite(x) || Math.abs(x) > LIMIT) throw new SymbolFormatError($t("{0}: geçersiz sayı", what));
  return Math.round(x * 100) / 100;
};
const str = (v: unknown, max: number) => String(v ?? '').replace(/[<>&"]/g, '').slice(0, max).trim();
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

export function validateCustomSymbol(input: unknown): CustomSymbol {
  if (!input || typeof input !== 'object') throw new SymbolFormatError($t("Sembol bir nesne olmalı."));
  const d = input as Record<string, any>;
  if (!isCustomKey(String(d.key))) throw new SymbolFormatError($t("Sembol anahtarı \"u_\" ile başlamalı (a–z, 0–9, _)."));
  const role = (['main', 'aux', 'plc', 'term'] as const).find((r) => r === d.role);
  if (!role) throw new SymbolFormatError($t("Sembol rolü main, aux, plc ya da term olmalı."));
  const pins: CustomPin[] = (Array.isArray(d.pins) ? d.pins : []).slice(0, 64).map((p: any, i: number) => ({
    x: n(p?.x, $t("Bağlantı noktası {0}", i + 1)), y: n(p?.y, $t("Bağlantı noktası {0}", i + 1)), label: str(p?.label, 12),
  }));
  const prims: Prim[] = (Array.isArray(d.prims) ? d.prims : []).slice(0, 500).map((p: any, i: number): Prim => {
    const w = $t("Çizim öğesi {0}", i + 1);
    switch (p?.k) {
      case 'line': {
        const pts = (Array.isArray(p.pts) ? p.pts : []).slice(0, 200).map((q: any) => [n(q?.[0], w), n(q?.[1], w)] as [number, number]);
        if (pts.length < 2) throw new SymbolFormatError($t("{0}: çizgi en az iki nokta ister", w));
        return { k: 'line', pts, dash: !!p.dash };
      }
      case 'rect':
        return { k: 'rect', x: n(p.x, w), y: n(p.y, w), w: Math.abs(n(p.w, w)), h: Math.abs(n(p.h, w)), fill: !!p.fill };
      case 'circle':
        return { k: 'circle', x: n(p.x, w), y: n(p.y, w), r: Math.abs(n(p.r, w)), fill: !!p.fill };
      case 'text':
        return { k: 'text', x: n(p.x, w), y: n(p.y, w), size: Math.min(20, Math.max(1, n(p.size ?? 2.5, w))), text: str(p.text, 40) };
      default:
        throw new SymbolFormatError($t("{0}: bilinmeyen tür", w));
    }
  });
  if (!pins.length && !prims.length) throw new SymbolFormatError($t("Sembolde en az bir çizim öğesi ya da bağlantı noktası olmalı."));
  const pass: [number, number][] = (Array.isArray(d.pass) ? d.pass : [])
    .map((p: any) => [Number(p?.[0]), Number(p?.[1])] as [number, number])
    .filter(([a, b]) => Number.isInteger(a) && Number.isInteger(b) && a >= 0 && b >= 0 && a < pins.length && b < pins.length && a !== b);
  return {
    key: String(d.key),
    name: str(d.name, 60) || $t("Adsız sembol"),
    cat: str(d.cat, 40) || CUSTOM_CAT,
    prefix: str(d.prefix, 4).toUpperCase().replace(/[^A-Z]/g, '') || 'U',
    role,
    kind: role === 'aux' ? (d.kind === 'nc' ? 'nc' : 'no') : undefined,
    mirror: role === 'main' && !!d.mirror,
    pins,
    pass,
    ...(typeof d.fn === 'string' && LIB[d.fn] && !isCustomKey(d.fn) ? { fn: d.fn } : {}),
    prims,
  };
}

/** Builds a library definition (with generated SVG) from a validated custom symbol. */
export function customToDef(c: CustomSymbol): SymDef {
  let svg = '';
  const xs: number[] = [], ys: number[] = [];
  const add = (x: number, y: number) => {
    xs.push(x);
    ys.push(y);
  };
  for (const p of c.prims) {
    if (p.k === 'line') {
      svg += `<path d="M${p.pts.map((q) => `${q[0]} ${q[1]}`).join('L')}"${p.dash ? ' class="dash"' : ''}/>`;
      p.pts.forEach((q) => add(q[0], q[1]));
    } else if (p.k === 'rect') {
      svg += p.fill
        ? `<path d="M${p.x} ${p.y}h${p.w}v${p.h}h${-p.w}Z" class="fill"/>`
        : `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}"/>`;
      add(p.x, p.y);
      add(p.x + p.w, p.y + p.h);
    } else if (p.k === 'circle') {
      svg += p.fill
        ? `<path d="M${p.x - p.r} ${p.y}A${p.r} ${p.r} 0 1 0 ${p.x + p.r} ${p.y}A${p.r} ${p.r} 0 1 0 ${p.x - p.r} ${p.y}Z" class="fill"/>`
        : `<circle cx="${p.x}" cy="${p.y}" r="${p.r}"/>`;
      add(p.x - p.r, p.y - p.r);
      add(p.x + p.r, p.y + p.r);
    } else {
      svg += `<text x="${p.x}" y="${p.y}" font-size="${p.size}" text-anchor="middle">${esc(p.text)}</text>`;
      const w = (p.text.length * p.size * 0.6) / 2;
      add(p.x - w, p.y - p.size);
      add(p.x + w, p.y);
    }
  }
  c.pins.forEach((p) => add(p.x, p.y));
  // a drawing imported before imports matched it (no fn): the library symbol it stands for, guessed the same way
  // from its connection points and its tag letter
  const fn = c.fn ?? (c.cat === 'EPLAN' ? guessFn({ role: c.role, kind: c.kind, labels: c.pins.map((p) => p.label), tag: c.prefix }) : undefined);
  const bb: BBox = xs.length ? [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)] : [-2, -2, 2, 2];
  let pass = c.pass?.length ? c.pass : undefined;
  if (!pass && (c.role === 'aux' || c.role === 'term') && c.pins.length % 2 === 0) {
    const h = c.pins.length / 2;
    pass = Array.from({ length: h }, (_, i) => [i, i + h] as [number, number]);
  }
  return {
    key: c.key, name: c.name, cat: c.cat, prefix: c.prefix, role: c.role, kind: c.kind,
    // a coil shows its contacts beside it, like the library's
    mirror: c.mirror || (c.role === 'main' && !!fn && !!LIB[fn]?.mirror),
    ...(fn ? { fn } : {}),
    pins: c.pins.map((p) => [p.x, p.y, p.label]), pass, bb, svg,
  };
}

/**
 * A symbol's name to show. A user or imported symbol matched to a library symbol is called by that one's name; an
 * imported one without a match by what it is (device, contact, terminal) and its connection points.
 */
export function symbolName(type: string): string {
  const L = LIB[type];
  if (!L) return type;
  if (L.fn && LIB[L.fn]) return $t(LIB[L.fn].name);
  if (L.cat === 'EPLAN') {
    const named = L.pins.map((p) => p[2]).filter(Boolean);
    const what = L.role === 'term' ? $t(LIB.term.name) : L.role === 'aux' ? $t(LIB[L.kind === 'nc' ? 'nc' : 'no'].name) : $t('Cihaz');
    if (L.role === 'term' || !named.length) return what;
    // its connection points, or how many there are when they are many (a card, a controller)
    return named.length <= 4 ? `${what} ${named.join('/')}` : `${what} · ${$t('{0} uç', L.pins.length)}`;
  }
  return $t(L.name);
}

const registry = new Map<string, CustomSymbol>();

export const customSymbol = (key: string) => registry.get(key);
export const customSymbols = () => [...registry.values()];

/** Adds (or replaces) user symbols in the shared library. Invalid entries are skipped. */
export function registerCustomSymbols(list: unknown[]): CustomSymbol[] {
  const ok: CustomSymbol[] = [];
  for (const raw of list) {
    try {
      const c = validateCustomSymbol(raw);
      registry.set(c.key, c);
      LIB[c.key] = customToDef(c);
      if (!LIB_ORDER.includes(c.key)) LIB_ORDER.push(c.key);
      ok.push(c);
    } catch {
      /* skip invalid symbol */
    }
  }
  return ok;
}

export function unregisterCustomSymbol(key: string) {
  registry.delete(key);
  delete LIB[key];
  const i = LIB_ORDER.indexOf(key);
  if (i >= 0) LIB_ORDER.splice(i, 1);
}

/** Stores the definitions of user symbols the project uses inside the project. */
export function embedCustomSymbols(pr: Project): Project {
  const used = new Set(pr.pages.flatMap((p) => p.syms.map((s) => s.type)).filter(isCustomKey));
  pr.symbols = [...used].map((k) => registry.get(k)).filter((c): c is CustomSymbol => !!c);
  return pr;
}

/* ---------------- black boxes ---------------- */

export interface BlackBoxSpec {
  name: string;
  prefix: string;
  top: string[];
  bottom: string[];
  left: string[];
  right: string[];
}

/** Splits "L1, L2, L3" into pin names. */
export const pinList = (v: string) => v.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean).slice(0, 32);

/**
 * A device drawn as a rectangle with connection points on its edges (EPLAN black box).
 * Pins sit on the 4 mm grid, 8 mm apart; the result is a regular user symbol.
 */
export function blackBoxSymbol(b: BlackBoxSpec): Omit<CustomSymbol, 'key'> {
  const P = 8, horiz = Math.max(b.top.length, b.bottom.length), vert = Math.max(b.left.length, b.right.length);
  const W = Math.max(24, (horiz + 1) * P), H = Math.max(16, (vert + 1) * P);
  const pins: CustomPin[] = [], prims: Prim[] = [{ k: 'rect', x: 0, y: 4, w: W, h: H }];
  b.top.forEach((l, i) => {
    const x = P * (i + 1);
    pins.push({ x, y: 0, label: l });
    prims.push({ k: 'line', pts: [[x, 0], [x, 4]] });
  });
  b.bottom.forEach((l, i) => {
    const x = P * (i + 1);
    pins.push({ x, y: H + 8, label: l });
    prims.push({ k: 'line', pts: [[x, H + 4], [x, H + 8]] });
  });
  b.left.forEach((l, i) => {
    const y = 4 + P * (i + 1);
    pins.push({ x: -4, y, label: l });
    prims.push({ k: 'line', pts: [[-4, y], [0, y]] });
  });
  b.right.forEach((l, i) => {
    const y = 4 + P * (i + 1);
    pins.push({ x: W + 4, y, label: l });
    prims.push({ k: 'line', pts: [[W, y], [W + 4, y]] });
  });
  const name = b.name.trim() || $t("Kara kutu");
  prims.push({ k: 'text', x: W / 2, y: 4 + H / 2 + 1, size: 2.5, text: name.slice(0, 40) });
  return { name, cat: $t("Kara kutular"), prefix: b.prefix || 'U', role: 'main', mirror: false, pins, pass: [], prims };
}
